import React from "react";
import { motion } from "framer-motion";
import SectionHeader from "../components/SectionHeader";

const whyPoints = [
  "Certified architects across AWS, Azure, GCP, and major data platforms",
  "Agile delivery with transparent sprint reporting and stakeholder dashboards",
  "Security-first architecture aligned with SOC 2, ISO 27001, and GDPR",
  "Dedicated engagement managers ensuring on-time, on-budget delivery",
  "Post-launch support, SLA-backed managed services, and knowledge transfer",
  "Proven ROI — average 3.2× return on technology investment across engagements",
];

const MissionBox = ({ title, text, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay }}
    className="rounded-xl p-6 mb-5"
    style={{
      background: "linear-gradient(135deg,rgba(26,107,255,.08),rgba(10,22,40,.6))",
      border: "1px solid rgba(26,107,255,.2)",
    }}
  >
    <h4 className="font-display font-bold text-white text-base mb-2">{title}</h4>
    <p className="text-sm leading-7" style={{ color: "var(--slate)" }}>{text}</p>
  </motion.div>
);

const About = () => {
  return (
    <section
      id="about"
      className="section-padding"
      style={{ background: "var(--navy)" }}
      aria-labelledby="about-title"
    >
      <div className="max-w-[1200px] mx-auto">
        <SectionHeader
          tag="Who We Are"
          title="Built for the Enterprise.<br/>Engineered for Growth."
        />

        <div
          className="grid gap-20 mt-12 items-start"
          style={{ gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))" }}
        >
          {/* Left */}
          <div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-sm leading-8 mb-5"
              style={{ color: "var(--slate)" }}
            >
              Tekstation was founded in 2010 with a clear mission: bridge
              the gap between complex enterprise technology challenges and pragmatic,
              scalable solutions. Over fifteen years, we've grown from a boutique
              cloud consultancy into a full-spectrum technology partner trusted by
              global enterprises.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-sm leading-8 mb-6"
              style={{ color: "var(--slate)" }}
            >
              Our multidisciplinary teams combine deep domain expertise with an
              agile, outcomes-first delivery model. We don't just consult — we
              architect, build, and operate alongside your teams to ensure lasting
              impact.
            </motion.p>

            <MissionBox
              title="Our Mission"
              text="To empower organizations with technology that is resilient, intelligent, and aligned to business strategy — delivering measurable ROI at every engagement."
              delay={0.25}
            />
            <MissionBox
              title="Our Vision"
              text="A world where technology complexity is invisible, and every business — regardless of size — can operate with the agility and intelligence of a tech-native company."
              delay={0.35}
            />
          </div>

          {/* Right */}
          <div>
            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="font-display font-bold text-white text-xl mb-6 tracking-tight"
              style={{ letterSpacing: "-0.02em" }}
            >
              Why Companies Choose Tekstation
            </motion.h3>
            <ul className="list-none space-y-4" aria-label="Reasons to choose Tekstation">
              {whyPoints.map((point, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.07 }}
                  className="flex items-start gap-3 text-sm leading-7"
                  style={{ color: "var(--steel)" }}
                  role="listitem"
                >
                  <div
                    className="w-[22px] h-[22px] flex items-center justify-center rounded-full flex-shrink-0 mt-[2px] text-[0.65rem]"
                    style={{
                      background: "rgba(26,107,255,.15)",
                      border: "1px solid rgba(26,107,255,.3)",
                      color: "var(--blue2)",
                    }}
                    aria-hidden="true"
                  >
                    ✓
                  </div>
                  {point}
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
